<template>
  <b-modal
    :id="modalId"
    title="Recebimento parcial de Conta?"
    centered
    size="lg"
    @show="prepararModal"
    @hidden="reset"
  >
    <b-row class="mb-4">
      <b-col>
        <b-form-checkbox
          name="recebimento-parcial-desconto"
          switch
          size="lg"
          v-model="descontoParcial"
        >
          Receber Parcial / Desconto
        </b-form-checkbox>
      </b-col>
    </b-row>

    <b-row>
      <b-col md="6">
        <b-form-group label="Documento">
          <b-form-input :value="contaAtual.documento" disabled />
        </b-form-group>
      </b-col>
      <b-col md="6">
        <b-form-group label="Cliente">
          <b-form-input :value="contaAtual.cliente" disabled />
        </b-form-group>
      </b-col>
      <b-col md="6">
        <b-form-group label="Vencimento">
          <b-form-input
            :value="formatDate(contaAtual.data_vencimento)"
            disabled
          />
        </b-form-group>
      </b-col>
      <b-col md="6">
        <b-form-group label="Recebimento">
          <b-form-input
            :value="formatDate(contaAtual.data_recebimento)"
            disabled
          />
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <b-form-group label="Valor R$">
          <b-form-input
            :value="numberToReal(parseFloat(contaAtual.valor || 0), 2)"
            disabled
          />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group label="Juros R$">
          <b-form-input
            :value="numberToReal(parseFloat(contaAtual.juros || 0), 2)"
            disabled
          />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group label="Multa R$">
          <b-form-input
            :value="numberToReal(parseFloat(contaAtual.multa || 0), 2)"
            disabled
          />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group label="Desconto R$">
          <b-form-input
            :value="numberToReal(parseFloat(contaAtual.desconto || 0), 2)"
            disabled
          />
        </b-form-group>
      </b-col>
    </b-row>

    <b-row class="border-bottom">
      <b-col md="3">
        <b-form-group label="Vlr. Receber R$">
          <b-form-input :value="numberToReal(valorTotal, 2)" disabled />
        </b-form-group>
      </b-col>
    </b-row>

    <b-row class="mt-4">
      <b-col>
        <b-form-group label="Data Vencimento">
          <b-form-datepicker
            v-model="dataVencimentoParcial"
            locale="pt-br"
            :date-format-options="dateFormatOptions"
          />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group label="Data Recebimento">
          <b-form-datepicker
            v-model="dataRecebimentoParcial"
            locale="pt-br"
            :date-format-options="dateFormatOptions"
          />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group label="Conta">
          <b-form-select v-model="contaParcial">
            <b-form-select-option
              v-for="banco in bancos"
              :key="`${banco.id_banco}-banco-parcial`"
              :value="banco"
            >
              {{ banco.nome }}
            </b-form-select-option>
          </b-form-select>
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group label="Meio de pagamento">
          <b-form-select v-model="meioPagamentoParcial">
            <b-form-select-option
              v-for="meio in meiosPagamentos"
              :key="`${meio.id}-meio-parcial`"
              :value="meio"
            >
              {{ meio.descricao }}
            </b-form-select-option>
          </b-form-select>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col md="3">
        <b-form-group
          label="Valor Recebido R$"
          :label-for="`${modalId}-valor-parcial`"
        >
          <money
            class="form-control"
            :id="`${modalId}-valor-parcial`"
            v-model="valorParcial"
            v-bind="configMask"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <b-row v-if="valorParcial > 0">
      <b-col v-if="!descontoParcial">
        Você irá gerar uma nova conta à receber no valor de
        <span class="font-weight-bold text-primary">
          R${{ numberToReal(valorRestante, 2) }}
        </span>
        ?
      </b-col>
      <b-col v-else>
        Você deseja gerar desconto no valor de
        <span class="font-weight-bold text-primary">
          R${{ numberToReal(valorRestante, 2) }}
        </span>
        ?
      </b-col>
    </b-row>

    <template #modal-footer="{ cancel }">
      <div>
        <b-button
          variant="primary"
          class="mr-5"
          :disabled="isSubmitting"
          @click="receberParcial"
        >
          {{ isSubmitting ? "Recebendo..." : "Receber" }}
        </b-button>
        <b-button :disabled="isSubmitting" @click="cancel()">
          Cancelar
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { Money } from "v-money";
import { MONEY as settingMoney } from "@/modules/Application";
import { SistemaMixin } from "@/modules/Application";

export default {
  name: "RecebimentoParcialModal",
  components: {
    Money,
  },
  mixins: [SistemaMixin],
  props: {
    modalId: {
      type: String,
      default: "receberParcial",
    },
    conta: {
      type: Object,
      default: null,
    },
    bancos: {
      type: Array,
      default: () => [],
    },
    meiosPagamentos: {
      type: Array,
      default: () => [],
    },
    recebimentoPdv: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      valorParcial: 0,
      dataRecebimentoParcial: null,
      dataVencimentoParcial: null,
      contaParcial: null,
      meioPagamentoParcial: null,
      descontoParcial: false,
      isSubmitting: false,
      configMask: settingMoney,
      dateFormatOptions: {
        year: "numeric",
        month: "numeric",
        day: "numeric",
      },
    };
  },
  computed: {
    contaAtual() {
      return this.conta || {};
    },
    valorTotal() {
      const valorTotal = parseFloat(this.conta?.valor_total || 0);
      return valorTotal > 0 ? valorTotal : parseFloat(this.conta?.valor || 0);
    },
    valorRestante() {
      return Math.max(this.valorTotal - Number(this.valorParcial || 0), 0);
    },
  },
  watch: {
    valorParcial(newValue) {
      if (newValue > this.valorTotal) {
        this.valorParcial = this.valorTotal;
      }
    },
  },
  methods: {
    prepararModal(event) {
      if (!this.conta?.id) {
        event?.preventDefault();
        this.$toast.error(
          "Selecione uma conta em aberto para receber parcialmente."
        );
        return;
      }

      this.dataVencimentoParcial = this.conta.data_vencimento;
      this.contaParcial = this.contaParcial || this.bancos[0] || null;
      this.meioPagamentoParcial =
        this.meioPagamentoParcial || this.meiosPagamentos[0] || null;
    },
    reset() {
      if (this.isSubmitting) return;

      this.valorParcial = 0;
      this.dataRecebimentoParcial = null;
      this.dataVencimentoParcial = null;
      this.contaParcial = null;
      this.meioPagamentoParcial = null;
      this.descontoParcial = false;
    },
    validar() {
      if (
        !this.dataRecebimentoParcial ||
        !this.dataVencimentoParcial ||
        !this.contaParcial ||
        !this.meioPagamentoParcial ||
        !this.valorParcial
      ) {
        this.$toast.error("Preencha todos os campos necessários!");
        return false;
      }

      if (Number(this.valorParcial) >= this.valorTotal) {
        this.$toast.error(
          "O valor parcial deve ser menor que o valor total da conta."
        );
        return false;
      }

      return true;
    },
    async receberParcial() {
      if (!this.validar() || this.isSubmitting) return;

      this.isSubmitting = true;
      try {
        let resultado;

        if (this.recebimentoPdv) {
          const { data } = await this.$http.post(
            "/contareceber/pdv/receber-parcial",
            {
              id_conta_receber: this.conta.id,
              valor_parcial: this.valorParcial,
              data_vencimento: this.dataVencimentoParcial,
              data_recebimento: this.dataRecebimentoParcial,
              id_banco: this.contaParcial.id_banco,
              id_meio_pagamento: this.meioPagamentoParcial.id,
              desconto: this.descontoParcial,
            }
          );
          resultado = data?.data || data;
        } else {
          const { data: dadosParcial } = await this.$http.post(
            "/contareceber/receber-parcial",
            {
              id_conta_receber: this.conta.id,
              valor_parcial: this.valorParcial,
            }
          );
          const { data } = await this.$http.post(
            "/contareceber/create-parcial",
            {
              ...dadosParcial,
              data_vencimento: this.dataVencimentoParcial,
              data_recebimento: this.dataRecebimentoParcial,
              id_banco: this.contaParcial.id_banco,
              id_meio_pagamento: this.meioPagamentoParcial.id,
              valor_parcial: this.valorParcial,
              desconto: this.descontoParcial,
            }
          );
          resultado = data;
        }

        this.$emit("success", resultado);
        this.$bvModal.hide(this.modalId);
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.msg ||
            "Não foi possível realizar o recebimento parcial."
        );
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>
